/**
 * C Feel Section — pill/popup activation, wheel sector highlight,
 * dose-tab + dose-select variant sync.
 *
 * Mirrors the public contract of how-does-it-feel-soul.js but with
 * c-feel-section BEM classes and a separate popup-card with title/
 * subtitle/content/tags + a mobile <select> that mirrors the desktop
 * tab buttons.
 */
(function () {
  var metafieldsCache = null;

  function getJsonUrl() {
    var meta = document.querySelector('meta[name="soul-metafields-url"]');
    return meta ? meta.getAttribute('content') : null;
  }

  function buildTitleVariantIndex(data) {
    if (!data || !data.by_variant_id || data._by_title_variant) return;
    var idx = {};
    Object.keys(data.by_variant_id).forEach(function (key) {
      var e = data.by_variant_id[key];
      if (e.product_title && e.variant_name) {
        idx[e.product_title + '::' + e.variant_name] = e;
      }
    });
    data._by_title_variant = idx;
  }

  function loadData() {
    if (metafieldsCache) return Promise.resolve(metafieldsCache);
    var url = getJsonUrl();
    if (!url) return Promise.resolve(null);
    return fetch(url)
      .then(function (res) { return res.json(); })
      .then(function (data) {
        buildTitleVariantIndex(data);
        metafieldsCache = data;
        return data;
      });
  }

  function getHelpsWithForVariant(data, variantId) {
    var entry = data && data.by_variant_id && data.by_variant_id[String(variantId)];
    return (entry && entry.helps_with) ? entry.helps_with : null;
  }

  function getHelpsWithForProduct(data, productId) {
    if (!data || !data.by_variant_id || !productId) return null;
    var ids = Object.keys(data.by_variant_id);
    for (var i = 0; i < ids.length; i++) {
      var entry = data.by_variant_id[ids[i]];
      if (String(entry.product_id) === String(productId) && entry.helps_with && entry.helps_with.length) {
        return entry.helps_with;
      }
    }
    return null;
  }

  function getHelpsWithByTitleAndVariantName(data, productTitle, variantName) {
    if (!data || !data._by_title_variant || !productTitle || !variantName) return null;
    var entry = data._by_title_variant[productTitle + '::' + variantName];
    return (entry && entry.helps_with) ? entry.helps_with : null;
  }

  function getHelpsWithByName(data, name) {
    if (!data || !data.by_variant_id) return null;
    var needle = name.trim().toLowerCase();
    var ids = Object.keys(data.by_variant_id);
    for (var i = 0; i < ids.length; i++) {
      var entry = data.by_variant_id[ids[i]];
      if (entry.variant_name && entry.variant_name.trim().toLowerCase() === needle) {
        return entry.helps_with || null;
      }
    }
    return null;
  }

  // Fallback for sections that aren't bound to a product (e.g. c-lp-feel-section
  // on page templates). The section can declare a CSV via `data-helps-with`,
  // listed in priority order, which the wheel renderer treats the same as
  // metafield-resolved helps_with.
  function getHelpsWithFromSection(sectionEl) {
    if (!sectionEl) return null;
    var csv = sectionEl.dataset.helpsWith;
    if (!csv) return null;
    var list = csv.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    return list.length ? list : null;
  }

  function resolveHelpsWith(data, variantId, productId, productTitle, variantName, sectionEl) {
    return getHelpsWithForVariant(data, variantId)
        || (productTitle && variantName && getHelpsWithByTitleAndVariantName(data, productTitle, variantName))
        || (productId && getHelpsWithForProduct(data, productId))
        || (variantName && getHelpsWithByName(data, variantName))
        || getHelpsWithFromSection(sectionEl);
  }

  // ── Wheel sector rendering ─────────────────────────────────────────────
  var WHEEL_RING_RADII = [23.8, 47.6, 71.4, 95.2, 119, 142.8, 166.6, 190.4, 214.2, 238];
  var WHEEL_RING_LEVELS = [10, 8, 6, 4];
  var WHEEL_FILL_DEFAULT = '#defeb8';
  var WHEEL_EXIT_MS = 230;

  // Optional per-section ring-intensity override. A section may declare
  // `data-ring-levels="10,8,6,4"` (one value per CSV priority slot, each
  // 1–10) to scale how far each sector extends. Used by the page variant
  // (c-lp-feel-section) so dose chips (Single/Double/Triple) can grow the
  // wheel. Absent on the PDP → falls back to the default levels unchanged.
  function getRingLevels(section) {
    var csv = section && section.dataset ? section.dataset.ringLevels : null;
    if (csv) {
      var arr = csv.split(',').map(function (s) { return parseInt(s.trim(), 10); })
                   .filter(function (n) { return n >= 1 && n <= 10; });
      if (arr.length) return arr;
    }
    return WHEEL_RING_LEVELS;
  }

  function wheelSectorPath(cx, cy, r, svgAngleDeg, spreadDeg) {
    var half = spreadDeg / 2;
    var saRad = (svgAngleDeg - half) * Math.PI / 180;
    var eaRad = (svgAngleDeg + half) * Math.PI / 180;
    var x1 = (cx + r * Math.cos(saRad)).toFixed(3);
    var y1 = (cy + r * Math.sin(saRad)).toFixed(3);
    var x2 = (cx + r * Math.cos(eaRad)).toFixed(3);
    var y2 = (cy + r * Math.sin(eaRad)).toFixed(3);
    return 'M ' + cx + ' ' + cy + ' L ' + x1 + ' ' + y1 +
           ' A ' + r + ' ' + r + ' 0 0 1 ' + x2 + ' ' + y2 + ' Z';
  }

  function renderWheel(section, helpsWith) {
    var group = section.querySelector('.feel-section__wheel-sectors');
    if (!group) return;

    function buildSectors() {
      while (group.firstChild) group.removeChild(group.firstChild);
      if (!helpsWith || !helpsWith.length) return;

      var posMap = {};
      helpsWith.forEach(function (tag, pos) {
        posMap[tag.toLowerCase().trim()] = pos;
      });

      var wheelFill = section.dataset.wheelFill || WHEEL_FILL_DEFAULT;
      var ringLevels = getRingLevels(section);
      var labels = section.querySelectorAll('.feel-section__pill[data-effect-index]');
      labels.forEach(function (label) {
        var effectIdx = parseInt(label.dataset.effectIndex, 10);
        var effectKey = (label.dataset.effectKey || label.textContent.trim()).toLowerCase();

        if (!posMap.hasOwnProperty(effectKey)) return;
        var priority = posMap[effectKey];
        if (priority >= ringLevels.length) return;

        var ringCount = ringLevels[priority];
        var fillR = WHEEL_RING_RADII[ringCount - 1];
        var svgAngle = effectIdx * 60 + 90;

        var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', wheelSectorPath(240, 240, fillR, svgAngle, 60));
        path.setAttribute('fill', wheelFill);
        group.appendChild(path);
      });
    }

    if (group._wheelTimer) {
      clearTimeout(group._wheelTimer);
      group._wheelTimer = null;
    }

    var existing = Array.from(group.children);
    if (existing.length === 0) {
      buildSectors();
    } else {
      existing.forEach(function (p) { p.classList.add('is-leaving'); });
      group._wheelTimer = setTimeout(function () {
        group._wheelTimer = null;
        buildSectors();
      }, WHEEL_EXIT_MS);
    }
  }

  // ── Popup card activation ──────────────────────────────────────────────
  function activatePopupByIndex(section, index) {
    var popups = section.querySelectorAll('.feel-section__popup');
    var pills  = section.querySelectorAll('.feel-section__pill[data-effect-index]');

    pills.forEach(function (p) {
      p.classList.remove('feel-section__pill--active');
      p.setAttribute('aria-pressed', 'false');
    });
    popups.forEach(function (p) { p.classList.remove('feel-section__popup--active'); });

    var activePill  = section.querySelector('.feel-section__pill[data-effect-index="' + index + '"]');
    var activePopup = section.querySelector('.feel-section__popup[data-popup-index="' + index + '"]');
    if (activePill) {
      activePill.classList.add('feel-section__pill--active');
      activePill.setAttribute('aria-pressed', 'true');
    }
    if (activePopup) activePopup.classList.add('feel-section__popup--active');
  }

  function activatePopupByKey(section, key) {
    var pills = section.querySelectorAll('.feel-section__pill[data-effect-index]');
    var match = null;
    pills.forEach(function (pill) {
      var pillKey = (pill.dataset.effectKey || pill.textContent.trim()).toLowerCase();
      if (pillKey === key) match = pill;
    });
    if (match) {
      activatePopupByIndex(section, parseInt(match.dataset.effectIndex, 10));
      return true;
    }
    return false;
  }

  function highlightEffects(section, helpsWith) {
    if (!helpsWith || !helpsWith.length) {
      renderWheel(section, helpsWith);
      return;
    }
    var firstTag = helpsWith[0].toLowerCase().trim();
    if (!activatePopupByKey(section, firstTag)) {
      // Fall back to first block if no match
      activatePopupByIndex(section, 0);
    }
    renderWheel(section, helpsWith);
  }

  // ── Variant sync ──────────────────────────────────────────────────────
  function syncProductForm(variantId) {
    if (!variantId) return;
    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    var idInput = form.querySelector('input[name="id"]');
    if (idInput && idInput.value !== String(variantId)) {
      idInput.value = variantId;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function applyVariantToSection(section, variantId, optionValue) {
    // Update desktop tabs
    var tabs = section.querySelectorAll('.feel-section__dose-tab');
    tabs.forEach(function (t) {
      t.classList.remove('feel-section__dose-tab--active');
      t.setAttribute('aria-pressed', 'false');
    });
    var tabMatch = null;
    if (optionValue) {
      tabMatch = section.querySelector('.feel-section__dose-tab[data-option-value="' + cssEscape(optionValue) + '"]');
    }
    if (!tabMatch && variantId) {
      tabMatch = section.querySelector('.feel-section__dose-tab[data-variant-id="' + variantId + '"]');
    }
    if (tabMatch) {
      tabMatch.classList.add('feel-section__dose-tab--active');
      tabMatch.setAttribute('aria-pressed', 'true');
    }

    // Update mobile select
    var select = section.querySelector('.feel-section__dose-select');
    if (select && variantId && select.value !== String(variantId)) {
      select.value = variantId;
    }

    // Resolve effects
    var pid = section.dataset.productId;
    var ptitle = section.dataset.productTitle;
    loadData().then(function (data) {
      var helpsWith = resolveHelpsWith(data, variantId, pid, ptitle, optionValue, section);
      if (helpsWith) highlightEffects(section, helpsWith);
    });
  }

  function cssEscape(s) {
    if (window.CSS && CSS.escape) return CSS.escape(s);
    return String(s).replace(/["\\]/g, '\\$&');
  }

  // ── Init ───────────────────────────────────────────────────────────────
  function initSection(section) {
    if (section._feelInit) return;
    section._feelInit = true;

    var pills = section.querySelectorAll('.feel-section__pill[data-effect-index]');
    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        var idx = parseInt(pill.dataset.effectIndex, 10);
        activatePopupByIndex(section, idx);
      });
      pill.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          pill.click();
        }
      });
    });

    // Desktop dose tabs
    var tabs = section.querySelectorAll('.feel-section__dose-tab');
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var vid = tab.dataset.variantId;
        var oval = tab.dataset.optionValue;
        applyVariantToSection(section, vid, oval);
        syncProductForm(vid);
      });
    });

    // Mobile dose select
    var select = section.querySelector('.feel-section__dose-select');
    if (select) {
      select.addEventListener('change', function () {
        var opt = select.options[select.selectedIndex];
        var vid = select.value;
        var oval = opt ? opt.getAttribute('data-option-value') : null;
        applyVariantToSection(section, vid, oval);
        syncProductForm(vid);
      });
    }

    // Initial highlight
    var pid = section.dataset.productId;
    var ptitle = section.dataset.productTitle;
    var urlVariantId = new URLSearchParams(window.location.search).get('variant');
    var activeTab = section.querySelector('.feel-section__dose-tab--active');
    var initialVid = (activeTab && activeTab.dataset.variantId) || urlVariantId || section.dataset.initialVariantId;
    var initialOval = activeTab ? activeTab.dataset.optionValue : null;

    loadData().then(function (data) {
      var helpsWith = resolveHelpsWith(data, initialVid, pid, ptitle, initialOval, section);
      if (helpsWith) highlightEffects(section, helpsWith);
      else activatePopupByIndex(section, 0); // ensure at least the first popup shows
    });

    // Subscribe to global variant changes
    var isProductPage = !!section.dataset.productId;
    if (isProductPage && typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var variant = event && event.data && event.data.variant;
        if (!variant || !variant.id) return;
        var doseEl = section.querySelector('.feel-section__dose[data-option-index]');
        if (doseEl) {
          var optionIndex = parseInt(doseEl.dataset.optionIndex, 10);
          var optionValue = variant['option' + (optionIndex + 1)];
          applyVariantToSection(section, variant.id, optionValue);
        } else {
          loadData().then(function (data) {
            var helpsWith = resolveHelpsWith(data, variant.id, section.dataset.productId, section.dataset.productTitle, null, section);
            if (helpsWith) highlightEffects(section, helpsWith);
          });
        }
      });
    }
  }

  function initAll(root) {
    (root || document).querySelectorAll('.feel-section').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
